import IncButton from 'components/inc-button/inc-button.component';
import {
  useForm,
  useFieldArray,
  FormProvider,
  Controller,
} from 'react-hook-form';
import EnvSelectField from './env-select-field/env-select-field.component';
import CustomInput from 'components/custom-input/custom-input.component';
import {
  Box,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import TrimTextField from 'components/form/form-fields/trim-text-field/trim-text-field.component';
import Form from 'components/form/form.component';
import RHFDevtoolsWrapper from 'components/rhf-devtools-wrapper/rhf-devtools-wrapper.component';
import { useTranslation } from 'react-i18next';
import { IncButtonDark } from 'components/inc-button/inc-button.styles';
import { useEnvForm } from 'components/env-config/env-drawer/env-form/env-form.hooks';
import { getEnv } from 'util/getEnv';
import Spinner from 'components/spinner/spinner.component';
import FormError from 'components/form-error/form-error.component';
import MandatoryFieldsInstruction from 'components/form/mandatory-fields-instruction/mandatory-fields-instruction.component';

const SQUARE_URL_SANDBOX = 'https://sandbox.web.squarecdn.com/v1/square.js';
const SQUARE_URL_PRODUCTION = 'https://web.squarecdn.com/v1/square.js';

const EnvForm = () => {
  const methods = useForm({
    defaultValues: {
      CLIENT_ALIAS: 'palmlaneqa',
      REACT_APP_RECAPTCHA_KEY: '',
      REACT_APP_SQUARE_URL: SQUARE_URL_SANDBOX,
      REACT_APP_MOBILE_API_URL: '',
      customHeaders: [],
    },
  });
  const { t } = useTranslation();

  const {
    environments,
    loading,
    onSubmit,
    resetEnv,
    setSelectedEnv,
    selectedEnv,
  } = useEnvForm();

  const {
    handleSubmit,
    register,
    control,
    formState: { errors, isSubmitting },
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'customHeaders',
  });

  return (
    <FormProvider {...methods}>
      {loading && <Spinner />}
      <Form onSubmit={handleSubmit(onSubmit)}>
        <MandatoryFieldsInstruction />

        <EnvSelectField
          environments={environments}
          setSelectedEnv={setSelectedEnv}
          selectedEnv={selectedEnv}
        />

        <CustomInput>
          <InputLabel htmlFor="apiUrl">
            {`${t('envConfig.form.baseUrl.label')} *`}
          </InputLabel>
          <TrimTextField
            id="apiUrl"
            fullWidth
            disabled={!!selectedEnv?.baseUrl}
            inputProps={{ 'aria-required': true }}
            {...register('REACT_APP_MOBILE_API_URL', { required: true })}
          />
          {errors.REACT_APP_MOBILE_API_URL && (
            <FormError errorMessage={t('forms.errors.requiredField')} />
          )}
        </CustomInput>

        <CustomInput>
          <InputLabel htmlFor="clientAlias">
            {t('envConfig.form.clientAlias.label')}
          </InputLabel>
          <TrimTextField
            id="clientAlias"
            fullWidth
            {...register('CLIENT_ALIAS')}
          />
        </CustomInput>

        <CustomInput>
          <InputLabel htmlFor="recaptchaKey">
            {t('envConfig.form.recaptchaKey.label')}
          </InputLabel>
          <TrimTextField
            id="recaptchaKey"
            {...register('REACT_APP_RECAPTCHA_KEY')}
            fullWidth
          />
        </CustomInput>

        <CustomInput>
          <InputLabel id="squareUrlLabel">
            {t('envConfig.form.squareUrl.label')}
          </InputLabel>
          <Controller
            name="REACT_APP_SQUARE_URL"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                id="squareUrl"
                labelId="squareUrlLabel"
                fullWidth
                SelectDisplayProps={{ 'data-testid': 'Select.SquareUrl' }}
                MenuProps={{
                  MenuListProps: { 'data-testid': 'Select.SquareUrl.Menu' },
                }}
              >
                <MenuItem value={SQUARE_URL_SANDBOX}>
                  {t('envConfig.form.squareUrl.sandbox')}
                </MenuItem>
                <MenuItem value={SQUARE_URL_PRODUCTION}>
                  {t('envConfig.form.squareUrl.production')}
                </MenuItem>
              </Select>
            )}
          />
        </CustomInput>

        <Box mt={2} mb={1}>
          <Typography variant="subtitle2" fontWeight="bold">
            Custom Headers
          </Typography>
          {fields.map((field, index) => (
            <Box
              key={field.id}
              display="flex"
              alignItems="center"
              gap={1}
              mt={1}
              data-testid={`CustomHeader.Row.${index}`}
            >
              <TrimTextField
                placeholder="Header name"
                size="small"
                {...register(`customHeaders.${index}.name`)}
                data-testid={`CustomHeader.Name.${index}`}
              />
              <TrimTextField
                placeholder="Header value"
                size="small"
                {...register(`customHeaders.${index}.value`)}
                data-testid={`CustomHeader.Value.${index}`}
              />
              <IconButton
                size="small"
                onClick={() => remove(index)}
                data-testid={`CustomHeader.Remove.${index}`}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Box mt={1}>
            <IncButtonDark
              size="small"
              onClick={() => append({ name: '', value: '' })}
              data-testid="CustomHeader.Add"
              startIcon={<AddIcon />}
            >
              Add Header
            </IncButtonDark>
          </Box>
        </Box>

        <IncButton
          type="submit"
          className="inlineSubmitButton"
          fullWidth
          disabled={isSubmitting}
        >
          {t('envConfig.form.buttons.submit')}
        </IncButton>

        {!!sessionStorage.getItem('env') && (
          <Box mt={0.5}>
            <IncButtonDark
              fullWidth
              onClick={resetEnv}
              data-testid="DynamicEnvironmentForm.IncButtonDark.Reset"
            >
              {t('envConfig.form.buttons.reset')}
            </IncButtonDark>
          </Box>
        )}

        <Box textAlign="center" paddingTop={1}>
          <Box fontWeight="bold">
            {t('envConfig.form.currentEnv.label')} :{' '}
            {getEnv('REACT_APP_ENV_NAME')}
          </Box>
          {getEnv('REACT_APP_ENV_DESCRIPTION')}
        </Box>

        <RHFDevtoolsWrapper control={control} />
      </Form>
    </FormProvider>
  );
};

export default EnvForm;
